import { reportUsage } from '@ms/yammer-data/dist/telemetry';
import { useIsTeamsTab } from 'hooks/selectors';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useIsDeepLinkPage } from './useIsDeepLinkPage';

type UseBackButtonProps = () => {
  readonly goBack: () => void;
  readonly showButton: boolean;
};

const rootPaths = [
  /^\/admin\/?$/,
  /^\/articles\/new$/,
  /^\/discover-groups\/explore((\/\w+)+|\/?)$/,
  /^\/feed((\/\w+)+|\/?)$/,
  /^\/inbox((\/\w+)+|\/?)$/,
  /^\/leaders\/?$/,
  /^\/leadership-corner((\/\w+)+|\/?)$/,
  /^\/storylines((\/\w+)+|\/?)$/,
  /^\/questions\/?$/,
  /^\/user-settings((\/\w+)+|\/?)$/,
  /^\/events\/[^/]+(\/questions|\/unanswered)?$/,
];

export const useBackButtonProps: UseBackButtonProps = () => {
  const history = useHistory();
  const location = useLocation();
  const [showButton, setShowButton] = useState(false);
  const isDeepLinkPage = useIsDeepLinkPage(location);
  const hasHistory = history.length > 1;
  const isTeamsTab = useIsTeamsTab();

  // when the app loads for the first time, a root state mark is pushed to the history state
  // see registerBackButton function in teams/registerBackButton.ts
  const isRootPathOnFirstLoad = isTeamsTab && !!window.history.state?.root;

  // when the app reloads the history state will be { state: undefined} and there is no root state mark
  // once the user navigates to a different page the history state will be { state: { prevPathname: 'some pathname' }}
  const isRootPathOnSubsequentLoad =
    isTeamsTab && !window.history.state?.root && !window.history.state?.state && hasHistory;
  const shouldHideBackButtonOnTeamsTab = isRootPathOnFirstLoad || isRootPathOnSubsequentLoad;

  const goBack = useCallback(() => {
    reportUsage({
      eventName: 'viva_engage_back_button_clicked',
      eventProperties: { backFrom: window.location.href },
    });
    history.goBack();
  }, [history]);

  useEffect(() => {
    if (isDeepLinkPage || !hasHistory || shouldHideBackButtonOnTeamsTab) {
      setShowButton(false);

      return;
    }

    const path = location.pathname;
    if (rootPaths.some((rootPath) => rootPath.test(path))) {
      setShowButton(false);
    } else {
      setShowButton(true);
    }
  }, [location, isDeepLinkPage, hasHistory, shouldHideBackButtonOnTeamsTab]);

  return {
    goBack,
    showButton,
  };
};
